<template>
  <div class="card calendar-card">
    <div class="card-body">
      <!-- Month navigation -->
      <div class="month-selector">
        <div class="year-selector">
          <button class="year-nav-btn" @click="changeYear(-1)"><i class="fas fa-chevron-left"></i></button>
          <span class="year-label">{{ currentDate.getFullYear() }}年</span>
          <button class="year-nav-btn" @click="changeYear(1)"><i class="fas fa-chevron-right"></i></button>
        </div>
        <div class="month-btn-group">
          <button v-for="m in 12" :key="m" :class="['month-btn', { active: currentDate.getMonth() === m - 1 }]" @click="selectMonth(m - 1)">{{ m }}月</button>
        </div>
      </div>

      <div class="archive-bar">
        <label class="archive-toggle">
          <input type="checkbox" v-model="showArchived" />
          <span>アーカイブ済みを表示</span>
        </label>
        <span v-if="showArchived" class="archive-note">
          削除済みスタッフを含めています。下部の集計もその分増えます。
        </span>
      </div>

      <!-- Calendar table with weekly subtotals -->
      <div class="calendar-table-wrap">
        <table class="table calendar-table">
          <thead>
            <tr>
              <th class="sticky-col">名前</th>
              <template v-for="(col, ci) in calendarColumns" :key="'h-' + ci">
                <th v-if="col.type === 'day'" :class="getDayHeaderClass(col.label)">
                  <div class="day-header">
                    <span class="day-num">{{ getDayNum(col.label) }}</span>
                    <span class="day-name">{{ getDayNameFromLabel(col.label) }}</span>
                  </div>
                </th>
                <th v-else-if="col.type === 'subtotal'" class="th-subtotal">{{ col.label }}</th>
                <th v-else-if="col.type === 'total'" class="th-total">合計</th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(entry) in sortedData" :key="entry.id">
              <td class="sticky-col staff-name-cell clickable-name" @click="openStaffModal(entry)">
                {{ entry.staff }}
                <span v-if="entry.deleted_at" class="badge-archived">アーカイブ済み</span>
              </td>
              <template v-for="(col, ci) in calendarColumns" :key="'c-' + ci">
                <td v-if="col.type === 'day'" :class="getDayCellClass(entry.data, col.label, entry.id)">
                  <!-- シフト予定 -->
                  <div v-if="getShiftForDay(entry.id, col.label)" class="cell-shift-row">
                    <span class="cell-shift-label">予</span>
                    <span class="cell-shift-time">{{ getShiftForDay(entry.id, col.label).start_time }}</span>
                    <span class="cell-shift-time">{{ getShiftForDay(entry.id, col.label).end_time }}</span>
                  </div>
                  <!-- 有給。半日有給は残り半日を勤務するため打刻と同じ日に並ぶ。
                       実績と排他にすると半休のセルが打刻だけになるので別々に出す -->
                  <span v-if="getLeaveLabel(entry.data, col.label)"
                        :class="['cell-leave', getLeaveStatusClass(entry.data, col.label)]">
                    {{ getLeaveLabel(entry.data, col.label) }}
                  </span>
                  <!-- 実績 -->
                  <div v-if="hasTimecard(entry.data, col.label)" class="cell-actual-row">
                    <span class="cell-actual-label">実</span>
                    <span class="cell-in" :class="getDiffClass(entry.id, col.label, 'in')">{{ getDayIn(entry.data, col.label) }}</span>
                    <span class="cell-out" :class="getDiffClass(entry.id, col.label, 'out')">{{ getDayOut(entry.data, col.label) }}</span>
                  </div>
                </td>
                <!-- 週小計（シフトパターンのみ） -->
                <td v-else-if="col.type === 'subtotal'" class="td-subtotal">
                  {{ calcWeeklyHours(entry, col.weekDays) }}
                </td>
                <!-- 月合計（シフトパターンのみ） -->
                <td v-else-if="col.type === 'total'" class="td-total">
                  {{ calcTotalHours(entry) }}
                </td>
              </template>
            </tr>
            <tr v-if="!sortedData || sortedData.length === 0">
              <td :colspan="calendarColumns.length + 1" class="text-center text-muted py-4">データがありません</td>
            </tr>
          </tbody>
          <tfoot v-if="sortedData.length > 0">
            <!-- 出勤数（企業パターン） -->
            <tr v-if="shiftPatternType !== 2" class="summary-row">
              <td class="sticky-col summary-label">出勤数</td>
              <template v-for="(col, ci) in calendarColumns" :key="'wc-' + ci">
                <td v-if="col.type === 'day'" class="summary-cell">{{ calcDayWorkCount(col.label) }}</td>
              </template>
            </tr>
            <!-- シフト時間（シフトパターンのみ） -->
            <tr v-if="shiftPatternType === 2" class="summary-row">
              <td class="sticky-col summary-label">シフト時間</td>
              <template v-for="(col, ci) in calendarColumns" :key="'sf-' + ci">
                <td v-if="col.type === 'day'" class="summary-cell">{{ calcDayShiftHours(col.label) }}</td>
                <td v-else-if="col.type === 'subtotal'" class="summary-cell td-subtotal">{{ calcWeekShiftHours(col.weekDays) }}</td>
                <td v-else class="summary-cell td-total">{{ calcMonthShiftHours() }}</td>
              </template>
            </tr>
            <!-- 実績時間（シフトパターンのみ） -->
            <tr v-if="shiftPatternType === 2" class="summary-row">
              <td class="sticky-col summary-label">実績時間</td>
              <template v-for="(col, ci) in calendarColumns" :key="'af-' + ci">
                <td v-if="col.type === 'day'" class="summary-cell">{{ calcDayActualHours(col.label) }}</td>
                <td v-else-if="col.type === 'subtotal'" class="summary-cell td-subtotal">{{ calcWeekActualHours(col.weekDays) }}</td>
                <td v-else class="summary-cell td-total">{{ calcMonthActualHours() }}</td>
              </template>
            </tr>
            <!-- シフトコスト（シフトパターンのみ） -->
            <tr v-if="shiftPatternType === 2" class="summary-row cost-row">
              <td class="sticky-col summary-label">シフトコスト</td>
              <template v-for="(col, ci) in calendarColumns" :key="'sc-' + ci">
                <td v-if="col.type === 'day'" class="summary-cell">{{ calcDayCost(col.label, 'shift') }}</td>
                <td v-else-if="col.type === 'subtotal'" class="summary-cell td-subtotal">{{ calcWeekCost(col.weekDays, 'shift') }}</td>
                <td v-else class="summary-cell td-total">{{ calcMonthCost('shift') }}</td>
              </template>
            </tr>
            <!-- 実績コスト（シフトパターンのみ） -->
            <tr v-if="shiftPatternType === 2" class="summary-row cost-row">
              <td class="sticky-col summary-label">実績コスト</td>
              <template v-for="(col, ci) in calendarColumns" :key="'ac-' + ci">
                <td v-if="col.type === 'day'" class="summary-cell">{{ calcDayCost(col.label, 'actual') }}</td>
                <td v-else-if="col.type === 'subtotal'" class="summary-cell td-subtotal">{{ calcWeekCost(col.weekDays, 'actual') }}</td>
                <td v-else class="summary-cell td-total">{{ calcMonthCost('actual') }}</td>
              </template>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
    <div class="card-footer calendar-footer">
      <button class="btn btn-primary csv-btn" :disabled="isCSVDisabled" @click="csvDownload">
        <i class="fas fa-download mr-2"></i>CSVダウンロード
      </button>
      <button class="btn btn-success csv-btn ml-2" :disabled="isCSVDisabled" @click="excelDownloadEach">
        <i class="fas fa-file-excel mr-2"></i>Excel（個別ZIP）
      </button>
      <button class="btn btn-success csv-btn ml-2" :disabled="isCSVDisabled" @click="excelDownloadMerged">
        <i class="fas fa-file-excel mr-2"></i>Excel（まとめ）
      </button>
    </div>
  </div>

  <!-- Staff Detail Modal -->
  <div v-if="showStaffModal" class="staff-modal-overlay" @click.self="showStaffModal = false">
    <div class="staff-modal">
      <div class="staff-modal-header">
        <h6 class="staff-modal-title">
          <i class="fas fa-user mr-2"></i>{{ modalStaff.name }}<span v-if="modalStaff.isArchived" class="badge-archived">アーカイブ済み</span> - {{ dateDisplayed }}
          <span v-if="shiftPatternType === 2 && modalSummary.hourlyWage" class="staff-modal-meta">
            時給 {{ Number(modalSummary.hourlyWage).toLocaleString() }}円
          </span>
          <span v-if="shiftPatternType === 2 && modalSummary.dailyTransportationAllowance" class="staff-modal-meta">
            交通費 {{ Number(modalSummary.dailyTransportationAllowance).toLocaleString() }}円/日
          </span>
        </h6>
        <div class="staff-modal-actions">
          <button class="btn btn-outline-secondary btn-sm" @click="exportStaffCSV"><i class="fas fa-file-csv mr-1"></i>CSV</button>
          <button class="btn btn-outline-secondary btn-sm" @click="exportStaffExcel"><i class="fas fa-file-excel mr-1"></i>Excel</button>
          <button class="staff-modal-close" @click="showStaffModal = false">&times;</button>
        </div>
      </div>
      <div class="staff-modal-body">
        <div class="table-responsive">
          <table class="table table-hover staff-detail-table">
            <thead>
              <tr>
                <th>日付</th>
                <th>種別</th>
                <th>出勤</th>
                <th>退勤</th>
                <th>休憩</th>
                <th>就業時間</th>
                <th>{{ shiftPatternType === 2 ? '8時間超' : '残業' }}</th>
                <th>深夜</th>
                <th>備考</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in modalRows" :key="row.date" :class="row.rowClass">
                <td class="td-date">{{ row.dateLabel }}</td>
                <td><span :class="row.typeClass">{{ row.typeLabel }}</span></td>
                <td>{{ row.timeStart }}</td>
                <td>{{ row.timeStop }}</td>
                <td>{{ row.breakHours }}</td>
                <td>{{ row.workingHours }}</td>
                <td :class="row.overtimeClass">{{ row.overtime }}</td>
                <td>{{ row.lateNight }}</td>
                <td class="td-notes"><span v-if="row.notes" :title="row.notes"><i class="fas fa-comment-alt mr-1 text-muted"></i>{{ row.notes.length > 10 ? row.notes.slice(0,10) + '...' : row.notes }}</span></td>
              </tr>
              <tr v-if="modalRows.length === 0">
                <td colspan="9" class="text-center text-muted py-3">データがありません</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <!-- Summary (footer - fixed at bottom) -->
      <div v-if="modalRows.length > 0" class="staff-summary">
        <div class="summary-item">
          <span class="summary-label2">出勤日数</span>
          <span class="summary-value">{{ modalSummary.workDays }}日</span>
        </div>
        <div class="summary-item">
          <span class="summary-label2">合計勤務時間</span>
          <span class="summary-value">{{ modalSummary.totalWork }}</span>
          <span v-if="shiftPatternType === 2 && modalSummary.basePay != null" class="summary-amount">
            {{ Number(modalSummary.basePay).toLocaleString() }}円
          </span>
        </div>
        <div class="summary-item">
          <span class="summary-label2">{{ shiftPatternType === 2 ? '8時間超合計' : '残業合計' }}</span>
          <span class="summary-value summary-overtime">
            {{ shiftPatternType === 2 ? modalSummary.totalBeyond8 : modalSummary.totalOvertime }}
          </span>
          <span v-if="shiftPatternType === 2 && modalSummary.beyond8Pay != null" class="summary-amount">
            {{ Number(modalSummary.beyond8Pay).toLocaleString() }}円
          </span>
        </div>
        <div class="summary-item">
          <span class="summary-label2">深夜合計</span>
          <span class="summary-value summary-latenight">{{ modalSummary.totalLateNight }}</span>
          <span v-if="shiftPatternType === 2 && modalSummary.lateNightPay != null" class="summary-amount">
            {{ Number(modalSummary.lateNightPay).toLocaleString() }}円
          </span>
        </div>
        <div v-if="shiftPatternType !== 2" class="summary-item">
          <span class="summary-label2">有給</span>
          <span class="summary-value">{{ modalSummary.paidLeaveDays }}日</span>
        </div>
        <template v-if="shiftPatternType === 2">
          <div class="summary-item">
            <span class="summary-label2">交通費合計</span>
            <span class="summary-value">{{ Number(modalSummary.transportationTotal).toLocaleString() }}円</span>
          </div>
          <div class="summary-item">
            <span class="summary-label2">支給合計</span>
            <span class="summary-value summary-payroll">{{ modalSummary.payrollTotal !== null ? Number(modalSummary.payrollTotal).toLocaleString() + '円' : '' }}</span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
const STORAGE_KEY = 'calendarPage.currentDate';

function restoreCurrentDate() {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      const d = new Date(saved);
      if (!isNaN(d.getTime())) return new Date(d.getFullYear(), d.getMonth(), 1);
    }
  } catch (e) { /* ignore */ }
  return new Date();
}

export default {
  data() {
    return {
      data: [],
      shiftData: {},
      defaultHourlyWage: 1000,
      shiftPatternType: 1,
      currentDate: restoreCurrentDate(),
      dateDisplayed : null,
      daysInMonth: [],
      company_plan: 1,
      navigatedDate: this.$moment(),
      showStaffModal: false,
      modalStaff: { name: '', isArchived: false, data: [] },
      showArchived: false,
    }
  },
  computed: {
    sortedData() {
      // スタッフID(entry.id)でキーイングする。氏名でキーイングすると、削除済み
      // スタッフを同じ氏名で再登録したケース（削除済み1名+在籍1名が同時に
      // 返る）で2行が1行に融合し、work_summary/deleted_at が先勝ちで
      // 片方だけ採用され、data[](打刻・有給)は両者分が混ざってしまう。
      // シフト予定の照合も 2026-08-12 にスタッフIDへ揃えた(MINNAKIN-17)。
      // loadShiftData() のルックアップも staff_id をキーにしている。
      const staffDataMap = new Map();

      this.data.forEach(entry => {
        const staffId = entry.id;
        const staffName = entry.name;

        if (!staffDataMap.has(staffId)) {
          staffDataMap.set(staffId, {
            id: staffId,
            staff: staffName,
            regular_working_hours_start: entry.regular_working_hours_start,
            regular_working_hours_stop: entry.regular_working_hours_stop,
            working_hours: entry.regular_working_hours,
            work_summary: entry.work_summary || null,
            deleted_at: entry.deleted_at || null,
            data: [],
          });
        }

        if (entry.timecard && entry.timecard.length > 0) {
          entry.timecard.forEach(timecardEntry => {
            staffDataMap.get(staffId).data.push({
              type: 'timecard',
              ...timecardEntry,
            });
          });
        }

        if (entry.paid_leaves && entry.paid_leaves.length > 0) {
          entry.paid_leaves.forEach(paidLeaveEntry => {
            staffDataMap.get(staffId).data.push({
              type: 'paid_leave',
              ...paidLeaveEntry,
            });
          });
        }
      });

      staffDataMap.forEach((staffData) => {
        staffData.data.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA - dateB;
        });
      });

      return Array.from(staffDataMap.values());
    },
    calendarColumns() {
      const columns = [];
      const isShiftPattern = this.shiftPatternType === 2;

      // 企業パターン: 日付列のみ
      if (!isShiftPattern) {
        this.daysInMonth.forEach(label => {
          columns.push({ type: 'day', label: label });
        });
        return columns;
      }

      // シフトパターン: 週小計 + 月合計あり
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      let weekDays = [];
      let weekNum = 1;

      this.daysInMonth.forEach((label, idx) => {
        const dayNum = parseInt(label.match(/\d+/)[0]);
        const date = new Date(year, month, dayNum);
        const dow = date.getDay();

        columns.push({ type: 'day', label: label });
        weekDays.push(label);

        // 土曜日 or 月末 → 小計列を挿入
        if (dow === 6 || idx === this.daysInMonth.length - 1) {
          columns.push({ type: 'subtotal', label: weekNum + '週', weekDays: [...weekDays] });
          weekDays = [];
          weekNum++;
        }
      });

      // 最後にトータル列
      columns.push({ type: 'total', label: '合計' });
      return columns;
    },
    modalRows() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      const daysCount = new Date(year, month + 1, 0).getDate();
      const wn = ['日','月','火','水','木','金','土'];
      const dataList = this.modalStaff.data || [];
      const rows = [];

      for (let day = 1; day <= daysCount; day++) {
        const dt = new Date(year, month, day);
        const dateLabel = (month + 1) + '/' + day + '(' + wn[dt.getDay()] + ')';
        // この日のデータを探す。半日有給は残り半日を勤務するため、同じ日に
        // 打刻と有給が並ぶ。1件だけ拾うと打刻が先に見つかって種別が「通常」に
        // なり半休だと分からなくなるので、打刻と有給を別々に取る。
        const dayItems = dataList.filter(item => this._isDataForDay(item, month + 1, day));
        const tc = dayItems.find(item => item.type === 'timecard');
        const leave = dayItems.find(item => item.type === 'paid_leave');

        if (!tc && !leave) {
          rows.push({
            date: dt.toISOString(), dateLabel, typeLabel: '休', typeClass: 'badge-type badge-type-off',
            timeStart: '', timeStop: '', breakHours: '', workingHours: '',
            overtime: '', lateNight: '', notes: '',
            overtimeClass: '', rowClass: 'row-off',
          });
          continue;
        }

        // シフトパターンでは「残業」（所定超過）ではなく「8時間超」を出す
        const overtime = tc
          ? (this.shiftPatternType === 2 ? (tc.beyond_eight_hours || '') : (tc.overtime_hours || ''))
          : '';
        const overtimeClass = overtime && this._timeToMins(overtime) >= 60 ? 'td-overtime-heavy' : (overtime ? 'td-overtime' : '');
        // 種別は有給を優先する（CSV/Excel 帳票 WorkCalendarBuilder::build() と同じ
        // 優先順）。半日有給は「半有」。勤務した分の打刻は列に残す。
        const isHalfLeave = !!leave && Number(leave.leave_type) === 2;
        let typeLabel = '通常';
        let typeClass = 'badge-type badge-type-normal';
        if (leave) {
          typeLabel = isHalfLeave ? '半有' : '有給';
          typeClass = isHalfLeave ? 'badge-type badge-type-half-leave' : 'badge-type badge-type-leave';
        }

        rows.push({
          date: (tc || leave).date, dateLabel, typeLabel, typeClass,
          timeStart: (tc && tc.time_start) || '', timeStop: (tc && tc.time_stop) || '',
          breakHours: (tc && tc.basic_break_hours) || '', workingHours: (tc && tc.working_hours) || '',
          overtime, lateNight: (tc && tc.late_night_hours) || '',
          notes: (tc && tc.notes) || (leave && leave.notes) || '',
          overtimeClass, rowClass: leave ? 'row-leave' : '',
        });
      }
      return rows;
    },
    modalSummary() {
      // 集計は API（WorkCalendarBuilder::aggregate）が計算したものをそのまま使う。
      // フロントで再計算すると Excel 出力と食い違うため、ここに計算は持たない。
      return this.modalStaff.summary || {
        workDays: 0, totalWork: '00:00', totalOvertime: '00:00',
        totalBeyond8: '00:00', totalLateNight: '00:00', paidLeaveDays: 0,
        hourlyWage: null, dailyTransportationAllowance: null,
        basePay: null, beyond8Pay: null, lateNightPay: null,
        transportationTotal: 0, payrollTotal: null,
      };
    },
    isButtonDisabled() {
      if (this.company_plan === 0) {
        if (this.$moment().format('YYYY-MM-DD') > this.navigatedDate.format('YYYY-MM-DD')) {
          return true;
        } else {
          if (this.$moment().format('YYYY-MM-DD') > this.navigatedDate.format('YYYY-MM-05')) {
            return true;
          }
          return false;
        }
      } else {
        return false;
      }
    },
    isCSVDisabled() {
      if (this.company_plan === 0) {
        if (this.$moment().format('YYYY-MM-DD') > this.navigatedDate.format('YYYY-MM-DD')) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    }
  },
  mounted() {
    this.updateDateDisplayed();
    this.updateDaysInMonth();
    this.getWorkCalendar(this.currentDate.getFullYear()+'-'+(this.currentDate.getMonth()+1)+'-01');
  },
  watch: {
    showArchived () {
      this.getWorkCalendar(this.currentDate.getFullYear() + '-' + (this.currentDate.getMonth() + 1) + '-01');
    },
  },
  methods: {
    changeYear(dir) {
      this.currentDate = new Date(this.currentDate.getFullYear() + dir, this.currentDate.getMonth(), 1);
      this.navigatedDate = this.$moment(this.currentDate);
      this._persistCurrentDate();
      this.updateDateDisplayed();
      this.updateDaysInMonth();
      this.getWorkCalendar(this.currentDate.getFullYear()+'-'+(this.currentDate.getMonth()+1)+'-01');
    },
    selectMonth(m) {
      this.currentDate = new Date(this.currentDate.getFullYear(), m, 1);
      this.navigatedDate = this.$moment(this.currentDate);
      this._persistCurrentDate();
      this.updateDateDisplayed();
      this.updateDaysInMonth();
      this.getWorkCalendar(this.currentDate.getFullYear()+'-'+(this.currentDate.getMonth()+1)+'-01');
    },
    _persistCurrentDate() {
      try {
        const d = this.currentDate.getFullYear() + '-' + String(this.currentDate.getMonth() + 1).padStart(2, '0') + '-01';
        sessionStorage.setItem(STORAGE_KEY, d);
      } catch (e) { /* ignore */ }
    },
    updateDateDisplayed () {
      this.dateDisplayed =
        this.currentDate.getFullYear() + '年' + (this.currentDate.getMonth() + 1) + '月';
    },
    getDayName (dayOfWeek) {
      const daysOfWeek = ['日', '月', '火', '水', '木', '金', '土'];
      return daysOfWeek[dayOfWeek];
    },
    updateDaysInMonth () {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth() + 1;
      const daysCount = new Date(year, month, 0).getDate();
      const dayLabels = [];

      for (let day = 1; day <= daysCount; day++) {
        const dayOfWeek = new Date(year, month - 1, day).getDay();
        const dayLabel = `${day}(${this.getDayName(dayOfWeek)})`;
        dayLabels.push(dayLabel);
      }
      this.daysInMonth = dayLabels;
    },
    getDayNum(label) {
      return label.match(/\d+/)[0];
    },
    getDayNameFromLabel(label) {
      const m = label.match(/\((.)\)/);
      return m ? m[1] : '';
    },
    getDayHeaderClass(label) {
      const name = this.getDayNameFromLabel(label);
      if (name === '日') return 'day-sun';
      if (name === '土') return 'day-sat';
      return '';
    },
    // モーダルの行と日付データの突き合わせ。date は "4/1"（n/j）形式と
    // "2026-04-01" 形式が混在する。
    _isDataForDay(item, month, day) {
      if (!item.date) return false;
      const dateStr = String(item.date);
      // "n/j" 形式 (例: "4/1")
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        return parseInt(parts[0]) === month && parseInt(parts[1]) === day;
      }
      // "yyyy-mm-dd" 形式
      if (dateStr.includes('-')) {
        const parts = dateStr.split(/[-T]/);
        return parseInt(parts[2]) === day;
      }
      // paid_leave_date等からの日付
      if (item.paid_leave_date) {
        const parts = String(item.paid_leave_date).split(/[-T]/);
        return parseInt(parts[2]) === day;
      }
      return false;
    },
    // 同じ日に打刻と有給が並ぶことがある（半日有給は残り半日を勤務するため）。
    // 欲しい方を取り違えないよう、呼び出し側は type を明示する。
    _findDataForDay(data, day, type = null) {
      const dayNumber = parseInt(day.match(/\d+/)[0]);
      return data.find(item => new Date(item.date).getDate() === dayNumber
        && (type === null || item.type === type));
    },
    hasTimecard(data, day) {
      return !!this._findDataForDay(data, day, 'timecard');
    },
    getLeaveStatusClass(data, day) {
      const d = this._findDataForDay(data, day, 'paid_leave');
      if (!d) return '';
      return d.approval_status === '承認済み' ? 'cell-leave-approved' : 'cell-leave-pending';
    },
    getLeaveLabel(data, day) {
      const d = this._findDataForDay(data, day, 'paid_leave');
      if (!d) return '';
      const base = (d.leave_type === 2) ? '半有' : '有休';
      return d.approval_status === '承認済み' ? base : base + '(待)';
    },
    getDayIn(data, day) {
      const d = this._findDataForDay(data, day, 'timecard');
      return d && d.time_start ? d.time_start : '';
    },
    getDayOut(data, day) {
      const d = this._findDataForDay(data, day, 'timecard');
      if (!d) return '';
      return d.time_stop ? d.time_stop : '未';
    },
    // シフト予定のルックアップキー。loadShiftData() が組み立てる
    // `staff_id + '-' + YYYY-MM-DD` と同じ形でなければならない。
    // 片方だけ変えると全件ミスし、シフト予定が画面から消える。
    _getDateKey(staffId, day) {
      const dayNumber = parseInt(day.match(/\d+/)[0]);
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth() + 1;
      return staffId + '-' + year + '-' + String(month).padStart(2, '0') + '-' + String(dayNumber).padStart(2, '0');
    },
    _getShiftForDay(staffId, day) {
      const key = this._getDateKey(staffId, day);
      return this.shiftData[key] || null;
    },
    _timeDiffMinutes(time1, time2) {
      if (!time1 || !time2) return 0;
      const [h1, m1] = time1.split(':').map(Number);
      const [h2, m2] = time2.split(':').map(Number);
      return Math.abs((h1 * 60 + m1) - (h2 * 60 + m2));
    },
    _calcHoursForDay(entry, dayLabel) {
      const d = this._findDataForDay(entry.data, dayLabel, 'timecard');
      if (!d) return 0;
      if (!d.time_start || !d.time_stop) return 0;
      const [h1, m1] = d.time_start.split(':').map(Number);
      const [h2, m2] = d.time_stop.split(':').map(Number);
      let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
      if (mins < 0) mins += 24 * 60;
      return mins;
    },
    _formatMinutes(totalMins) {
      if (totalMins <= 0) return '-';
      const h = Math.floor(totalMins / 60);
      const m = totalMins % 60;
      return h + ':' + String(m).padStart(2, '0');
    },
    calcWeeklyHours(entry, weekDays) {
      let total = 0;
      weekDays.forEach(day => { total += this._calcHoursForDay(entry, day); });
      return this._formatMinutes(total);
    },
    calcTotalHours(entry) {
      let total = 0;
      this.daysInMonth.forEach(day => { total += this._calcHoursForDay(entry, day); });
      return this._formatMinutes(total);
    },
    // --- Day/Week/Month summary calculations ---
    _shiftMinsForDay(dayLabel) {
      let total = 0;
      this.sortedData.forEach(entry => {
        const shift = this._getShiftForDay(entry.id, dayLabel);
        if (shift) {
          const [h1,m1] = shift.start_time.split(':').map(Number);
          const [h2,m2] = shift.end_time.split(':').map(Number);
          let mins = (h2*60+m2) - (h1*60+m1);
          if (mins < 0) mins += 24*60;
          total += mins;
        }
      });
      return total;
    },
    _actualMinsForDay(dayLabel) {
      let total = 0;
      this.sortedData.forEach(entry => {
        total += this._calcHoursForDay(entry, dayLabel);
      });
      return total;
    },
    _formatHoursDec(mins) {
      if (mins <= 0) return '-';
      return (mins / 60).toFixed(1) + 'h';
    },
    _formatCost(mins) {
      if (mins <= 0) return '-';
      const hours = mins / 60;
      const cost = Math.round(hours * this.defaultHourlyWage);
      return '¥' + cost.toLocaleString();
    },
    calcDayWorkCount(dayLabel) {
      let count = 0;
      this.sortedData.forEach(entry => {
        if (this._findDataForDay(entry.data, dayLabel, 'timecard')) count++;
      });
      return count || '-';
    },
    calcDayShiftHours(dayLabel) { return this._formatHoursDec(this._shiftMinsForDay(dayLabel)); },
    calcDayActualHours(dayLabel) { return this._formatHoursDec(this._actualMinsForDay(dayLabel)); },
    calcWeekShiftHours(weekDays) { let t=0; weekDays.forEach(d => t+=this._shiftMinsForDay(d)); return this._formatHoursDec(t); },
    calcWeekActualHours(weekDays) { let t=0; weekDays.forEach(d => t+=this._actualMinsForDay(d)); return this._formatHoursDec(t); },
    calcMonthShiftHours() { let t=0; this.daysInMonth.forEach(d => t+=this._shiftMinsForDay(d)); return this._formatHoursDec(t); },
    calcMonthActualHours() { let t=0; this.daysInMonth.forEach(d => t+=this._actualMinsForDay(d)); return this._formatHoursDec(t); },
    calcDayCost(dayLabel, type) { return this._formatCost(type==='shift' ? this._shiftMinsForDay(dayLabel) : this._actualMinsForDay(dayLabel)); },
    calcWeekCost(weekDays, type) { let t=0; weekDays.forEach(d => t+=(type==='shift' ? this._shiftMinsForDay(d) : this._actualMinsForDay(d))); return this._formatCost(t); },
    calcMonthCost(type) { let t=0; this.daysInMonth.forEach(d => t+=(type==='shift' ? this._shiftMinsForDay(d) : this._actualMinsForDay(d))); return this._formatCost(t); },
    // 現在どこからも呼ばれていない（2026-08-12 時点）。氏名ベースのルックアップを
    // 1つだけ残すと将来使われたときに同じ取り違えが再発するため、引数を揃えてある。
    getShiftTime(staffId, day) {
      const shift = this._getShiftForDay(staffId, day);
      if (!shift) return '';
      return shift.start_time + '-' + shift.end_time;
    },
    getShiftForDay(staffId, day) {
      return this._getShiftForDay(staffId, day);
    },
    getDiffClass(staffId, day, type) {
      const shift = this._getShiftForDay(staffId, day);
      if (!shift) return '';
      const d = this._findDataForDay(
        this.sortedData.find(s => s.id === staffId)?.data || [], day, 'timecard'
      );
      if (!d) return '';

      const actual = type === 'in' ? d.time_start : d.time_stop;
      const scheduled = type === 'in' ? shift.start_time : shift.end_time;
      if (!actual || !scheduled) return '';

      const diff = this._timeDiffMinutes(actual, scheduled);
      if (diff >= 30) return 'diff-large';
      if (diff >= 10) return 'diff-medium';
      return '';
    },
    getDayCellClass(data, day, staffId) {
      // 半休の日は打刻と有給が両方ある。有給の背景色と打刻ありの装飾は
      // どちらも付けたいので、種別ごとに取る。
      const timecard = this._findDataForDay(data, day, 'timecard');
      const leave = this._findDataForDay(data, day, 'paid_leave');
      const name = this.getDayNameFromLabel(day);
      const classes = [];
      if (name === '日') classes.push('day-sun');
      if (name === '土') classes.push('day-sat');
      if (leave) classes.push('cell-leave-bg');
      if (timecard) classes.push('cell-has-data');
      // シフトとの差異が大きい場合
      if (timecard && staffId != null) {
        const shift = this._getShiftForDay(staffId, day);
        if (shift) {
          const diffIn = this._timeDiffMinutes(timecard.time_start, shift.start_time);
          const diffOut = timecard.time_stop ? this._timeDiffMinutes(timecard.time_stop, shift.end_time) : 0;
          if (diffIn >= 30 || diffOut >= 30) classes.push('cell-diff-large');
        }
      }
      return classes.join(' ');
    },
    getDataForDay(timecard, day) {
      const dayNumber = parseInt(day.match(/\d+/)[0]);
      const dataForDay = timecard.find(item => {
        const itemDay = new Date(item.date).getDate();
        return itemDay === dayNumber;
      });
      if (dataForDay) {
        if (dataForDay.type === 'timecard') {
          const time_in = dataForDay.time_start;
          const time_out = dataForDay.time_stop ? dataForDay.time_stop : '未';
          return time_in + ' - ' + time_out;
        }
        else {
          return '有給休暇';
        }
      } else {
        return '';
      }
    },
    getWorkCalendar (date) {
      const shop_id = this.$store.getters.shop;
      // アーカイブ済み（削除済み）スタッフを含めるかはトグルで切り替える。
      // 既存のスタッフ一覧・店舗一覧と同じ with_trashed パラメータを使う。
      const params = this.showArchived ? { with_trashed: 1 } : {};
      this.$http.get(process.env.VUE_APP_API_PATH + '/shop/workCalendar/' + shop_id + '/' + date, { params })
      .then(response => {
        this.data = response.data.data.staff;
        this.company_plan = response.data.data.company_plan;
        this.defaultHourlyWage = response.data.data.default_hourly_wage || 1000;
        this.loadShiftData(date);
      })
      .catch(error => {
        console.log(error);
      });
    },
    loadShiftData(date) {
      const shop_id = this.$store.getters.shop;
      this.$http.get(process.env.VUE_APP_API_PATH + '/shop/shift/' + shop_id + '/' + date)
      .then(response => {
        this.shiftPatternType = response.data.data.shift_pattern_type || 1;
        const staffList = response.data.data.staff || [];
        const lookup = {};
        staffList.forEach(s => {
          Object.keys(s.shifts || {}).forEach(dateKey => {
            const shift = s.shifts[dateKey];
            // 出勤(1), 半休(4), 半日有給(5) で時刻が設定されているもの。
            // 休(2)・全日有給(3)は勤務時間なしのため除外
            if (shift && shift.start_time && shift.end_time
                && [1, 4, 5].includes(shift.shift_type)) {
              // 氏名ではなくスタッフIDをキーにする。同姓同名がいると氏名キーでは
              // 後勝ちで1件しか保持できず、同名の全員が同じ予定を引いてしまう。
              lookup[s.staff_id + '-' + dateKey] = {
                shift_type: shift.shift_type,
                start_time: shift.start_time,
                end_time: shift.end_time,
              };
            }
          });
        });
        this.shiftData = lookup;
      })
      .catch(() => {});
    },
    csvDownload() {
      const shop_id = this.$store.getters.shop;
      const date = this.currentDate.getFullYear() + '-' + (this.currentDate.getMonth() + 1) + '-01';

      this.$http.get(process.env.VUE_APP_API_PATH + '/shop/csvDownload/' + shop_id + '/' + date, {
        responseType: 'blob'
      })
      .then(response => {
        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'staff-csv-files.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.log(error);
      });
    },
    excelDownloadEach() {
      const shop_id = this.$store.getters.shop;
      const date = this.currentDate.getFullYear() + '-' + (this.currentDate.getMonth() + 1) + '-01';

      this.$http.get(process.env.VUE_APP_API_PATH + '/shop/excelDownload/' + shop_id + '/' + date, {
        responseType: 'blob'
      })
      .then(response => {
        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'staff-excel-files.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.log(error);
      });
    },
    excelDownloadMerged() {
      const shop_id = this.$store.getters.shop;
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth() + 1;
      const date = year + '-' + month + '-01';

      this.$http.get(process.env.VUE_APP_API_PATH + '/shop/excelDownloadMerged/' + shop_id + '/' + date, {
        responseType: 'blob'
      })
      .then(response => {
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'staff-timecard_' + year + '-' + String(month).padStart(2, '0') + '.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.log(error);
      });
    },
    viewDetails () {
      this.$router.push({name:'CompanyShopDetailsPage'});
    },
    _timeToMins(str) {
      if (!str) return 0;
      const parts = str.split(':');
      return parseInt(parts[0] || 0) * 60 + parseInt(parts[1] || 0);
    },
    openStaffModal(entry) {
      // isArchived は name とは別に持つ。modalStaff.name は
      // モーダル内の CSV / Excel ダウンロードのファイル名にも使われるため、
      // バッジの文字列を name に混ぜるとファイル名が汚れる。
      this.modalStaff = {
        name: entry.staff,
        isArchived: !!entry.deleted_at,
        data: entry.data,
        summary: entry.work_summary || null,
      };
      this.showStaffModal = true;
    },
    _buildExportData() {
      const isShift = this.shiftPatternType === 2;
      const headers = ['日付', '種別', '出勤', '退勤', '休憩', '就業時間', isShift ? '8時間超' : '残業', '深夜', '備考'];
      const rows = this.modalRows.map(r => [r.dateLabel, r.typeLabel, r.timeStart, r.timeStop, r.breakHours, r.workingHours, r.overtime, r.lateNight, r.notes]);
      rows.push([]);
      const yen = (v) => (v !== null && v !== undefined ? Number(v).toLocaleString() + '円' : '');
      rows.push(['出勤日数', this.modalSummary.workDays + '日']);
      if (isShift) {
        // 時間の行に金額を併記する（画面のカードと同じ並び）。行によって列数が変わる。
        rows.push(['合計勤務時間', this.modalSummary.totalWork, yen(this.modalSummary.basePay)]);
        rows.push(['8時間超合計', this.modalSummary.totalBeyond8, yen(this.modalSummary.beyond8Pay)]);
        rows.push(['深夜合計', this.modalSummary.totalLateNight, yen(this.modalSummary.lateNightPay)]);
        rows.push(['交通費合計', yen(this.modalSummary.transportationTotal)]);
        rows.push(['支給合計', yen(this.modalSummary.payrollTotal)]);
      } else {
        rows.push(['合計勤務時間', this.modalSummary.totalWork]);
        rows.push(['残業合計', this.modalSummary.totalOvertime]);
        rows.push(['深夜合計', this.modalSummary.totalLateNight]);
        rows.push(['有給', this.modalSummary.paidLeaveDays + '日']);
      }
      return { headers, rows };
    },
    exportStaffCSV() {
      const { headers, rows } = this._buildExportData();
      const bom = '\uFEFF';
      let csv = bom + headers.join(',') + '\n';
      rows.forEach(r => { csv += r.map(v => '"' + String(v || '').replace(/"/g, '""') + '"').join(',') + '\n'; });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = this.modalStaff.name + '_' + this.dateDisplayed + '.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
    exportStaffExcel() {
      const { headers, rows } = this._buildExportData();
      let table = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1">';
      table += '<tr>' + headers.map(h => '<th style="background:#f0f0f0;font-weight:bold;">' + h + '</th>').join('') + '</tr>';
      rows.forEach(r => {
        if (r.length === 0) { table += '<tr><td colspan="' + headers.length + '"></td></tr>'; return; }
        table += '<tr>' + r.map(v => '<td>' + (v || '') + '</td>').join('') + '</tr>';
      });
      table += '</table></body></html>';
      const blob = new Blob([table], { type: 'application/vnd.ms-excel;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = this.modalStaff.name + '_' + this.dateDisplayed + '.xls';
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
  /* Month selector */
  .month-selector { margin-bottom: 16px; }
  .year-selector { display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 8px; }
  .year-nav-btn { width: 32px; height: 32px; border-radius: 50%; border: 1.5px solid #d2d6dc; background: #fff; color: #4a5568; font-size: 12px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
  .year-nav-btn:hover { background: #049C4C; border-color: #049C4C; color: #fff; }
  .year-label { font-size: 20px; font-weight: 700; min-width: 80px; text-align: center; }
  .month-btn-group { display: flex; flex-wrap: wrap; justify-content: center; gap: 4px; }
  .month-btn { padding: 6px 12px; font-size: 13px; font-weight: 600; border: 1.5px solid #d2d6dc; border-radius: 6px; background: #fff; color: #4a5568; cursor: pointer; transition: all 0.15s; min-width: 48px; }
  .month-btn:hover { background: #edf2f7; border-color: #a0aec0; }
  .month-btn.active { background: #049C4C; color: #fff; border-color: #049C4C; }

  /* Calendar table wrapper */
  .calendar-card :deep(.card-body) {
    overflow: visible;
  }

  .calendar-table-wrap {
    overflow: auto !important;
    max-height: calc(100vh - 260px);
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    position: relative;
  }

  .calendar-table thead {
    position: sticky;
    top: 0;
    z-index: 10;
  }

  /* Table */
  .calendar-table {
    margin: 0;
    border-collapse: separate;
    border-spacing: 0;
    min-width: 100%;
    white-space: nowrap;
  }

  .calendar-table thead th {
    background: #f7fafc;
    border-bottom: 2px solid #e2e8f0;
    padding: 8px 6px;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .calendar-table tbody td {
    padding: 4px 4px;
    text-align: center;
    font-size: 11px;
    border-bottom: 1px solid #edf2f7;
    border-right: 1px solid #f0f0f0;
    vertical-align: middle;
    min-height: 56px;
    min-width: 70px;
  }

  .calendar-table tbody tr:hover td {
    background-color: #f0faf4;
  }

  /* Sticky name column */
  .sticky-col {
    position: sticky;
    left: 0;
    z-index: 2;
    background: #fff;
    border-right: 2px solid #e2e8f0 !important;
    min-width: 100px;
    max-width: 130px;
  }

  thead .sticky-col {
    z-index: 11;
    background: #f7fafc;
  }

  .staff-name-cell {
    font-weight: 600;
    font-size: 13px;
    color: #1a202c;
    text-align: left;
    padding-left: 12px !important;
  }

  /* Day header */
  .day-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.3;
  }

  .day-num {
    font-size: 13px;
    font-weight: 700;
  }

  .day-name {
    font-size: 10px;
    font-weight: 500;
    color: #718096;
  }

  /* Weekend colors */
  .day-sun, .day-sun .day-num {
    color: #e53e3e !important;
    background-color: #fff5f5;
  }

  .day-sat, .day-sat .day-num {
    color: #3182ce !important;
    background-color: #ebf8ff;
  }

  thead .day-sun {
    background-color: #fff5f5 !important;
  }

  thead .day-sat {
    background-color: #ebf8ff !important;
  }

  /* Cell content */
  .cell-timecard {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    line-height: 1.3;
  }

  .cell-in {
    color: #049C4C;
    font-weight: 600;
    font-size: 12px;
  }

  .cell-out {
    color: #3182ce;
    font-weight: 600;
    font-size: 12px;
  }

  .cell-leave {
    display: inline-block;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 3px;
  }

  /* 承認済み: 青 */
  .cell-leave-approved { background: #3182ce; }
  /* 承認待ち: 黄(オレンジ系) */
  .cell-leave-pending { background: #ed8936; }

  .cell-leave-bg {
    background-color: #fffaf0 !important;
  }

  /* Subtotal & Total columns */
  .th-subtotal {
    background: #edf2f7 !important;
    font-size: 10px !important;
    font-weight: 700;
    color: #4a5568;
    min-width: 50px;
    text-align: center;
    border-left: 2px solid #cbd5e0 !important;
  }

  .th-total {
    background: #e2e8f0 !important;
    font-size: 11px !important;
    font-weight: 700;
    color: #1a202c;
    min-width: 55px;
    text-align: center;
    border-left: 2px solid #a0aec0 !important;
  }

  .td-subtotal {
    background: #f7fafc;
    font-size: 12px;
    font-weight: 700;
    color: #2d3748;
    text-align: center;
    vertical-align: middle;
    border-left: 2px solid #cbd5e0 !important;
    white-space: nowrap;
  }

  .td-total {
    background: #edf2f7;
    font-size: 13px;
    font-weight: 700;
    color: #049C4C;
    text-align: center;
    vertical-align: middle;
    border-left: 2px solid #a0aec0 !important;
    white-space: nowrap;
  }

  /* Summary rows */
  .summary-row td {
    background: #f7fafc;
    border-top: 1px solid #e2e8f0;
  }

  .summary-label {
    font-size: 10px !important;
    font-weight: 700;
    color: #4a5568;
    text-align: left !important;
    padding-left: 8px !important;
    background: #f7fafc !important;
  }

  .summary-cell {
    font-size: 10px;
    font-weight: 600;
    color: #2d3748;
    text-align: center;
    white-space: nowrap;
    vertical-align: middle;
  }

  .cost-row td {
    background: #edf2f7;
  }

  .cost-row .summary-label {
    background: #edf2f7 !important;
  }

  .cost-row .summary-cell {
    color: #049C4C;
    font-weight: 700;
  }

  .cell-has-data {
    background-color: #f0fff4;
  }

  .cell-diff-large {
    background-color: #fff5f5 !important;
    border: 1px solid #fed7d7 !important;
  }

  /* Shift scheduled row */
  .cell-shift-row {
    display: flex;
    align-items: center;
    gap: 3px;
    justify-content: center;
    padding: 2px 0;
    border-bottom: 1px dashed #e2e8f0;
    margin-bottom: 2px;
  }

  .cell-shift-label {
    font-size: 9px;
    font-weight: 700;
    color: #fff;
    background: #a0aec0;
    padding: 0 3px;
    border-radius: 2px;
    line-height: 1.5;
  }

  .cell-shift-time {
    font-size: 11px;
    font-weight: 600;
    color: #718096;
  }

  /* Actual row */
  .cell-actual-row {
    display: flex;
    align-items: center;
    gap: 3px;
    justify-content: center;
    padding: 2px 0;
  }

  .cell-actual-label {
    font-size: 9px;
    font-weight: 700;
    color: #fff;
    background: #049C4C;
    padding: 0 3px;
    border-radius: 2px;
    line-height: 1.5;
  }

  /* Time diff highlighting */
  .diff-medium {
    color: #d69e2e !important;
    font-weight: 700 !important;
  }

  .diff-large {
    color: #e53e3e !important;
    font-weight: 700 !important;
  }

  /* Footer */
  .calendar-footer {
    display: flex;
    justify-content: center;
    padding: 16px 24px;
    background: #f7fafc;
    border-top: 1px solid #e2e8f0;
    border-radius: 0 0 10px 10px;
  }

  .csv-btn {
    min-width: 220px;
    padding: 10px 24px;
    font-size: 15px;
  }

  /* Clickable name */
  .clickable-name {
    cursor: pointer;
    transition: color 0.15s;
  }
  .clickable-name:hover {
    color: #049C4C !important;
    text-decoration: underline;
  }

  /* Staff modal */
  .staff-modal-overlay {
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    background: rgba(0,0,0,0.45); z-index: 1050;
    display: flex; align-items: center; justify-content: center;
  }
  .staff-modal {
    background: #fff; border-radius: 12px; width: 860px; max-width: 95vw; max-height: 85vh;
    box-shadow: 0 20px 60px rgba(0,0,0,0.25); display: flex; flex-direction: column; overflow: hidden;
  }
  .staff-modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 20px; border-bottom: 1px solid #e2e8f0; flex-shrink: 0;
  }
  .staff-modal-title { font-weight: 700; font-size: 16px; margin: 0; color: #1a202c; }
  .staff-modal-meta { margin-left: 12px; font-size: 12px; font-weight: 400; color: #6c757d; }
  .staff-modal-actions { display: flex; align-items: center; gap: 6px; }
  .staff-modal-close { border: none; background: none; font-size: 24px; color: #718096; cursor: pointer; padding: 0 4px; line-height: 1; }
  .staff-modal-close:hover { color: #e53e3e; }
  .staff-modal-body { overflow-y: auto; padding: 0; flex: 1; }

  .staff-detail-table { margin: 0; }
  .staff-detail-table thead th {
    font-size: 12px; font-weight: 700; color: #4a5568; background: #f7fafc;
    border-bottom: 2px solid #e2e8f0; padding: 10px 8px; white-space: nowrap; position: sticky; top: 0; z-index: 1;
  }
  .staff-detail-table tbody td {
    padding: 8px; font-size: 13px; vertical-align: middle; border-color: #edf2f7;
  }
  .staff-detail-table .td-date { font-weight: 600; color: #1a202c; white-space: nowrap; }
  .staff-detail-table .td-notes { max-width: 100px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #718096; font-size: 12px; }
  .staff-detail-table .td-overtime { color: #d69e2e; font-weight: 600; }
  .staff-detail-table .td-overtime-heavy { color: #e53e3e; font-weight: 700; }
  .staff-detail-table .row-leave { background: #fffaf0; }

  .badge-type { font-size: 11px; font-weight: 600; padding: 2px 7px; border-radius: 4px; }
  .badge-type-normal { background: #edf2f7; color: #4a5568; }
  .badge-type-leave { background: #fefcbf; color: #744210; }
  /* 半日有給。全日の有給(黄)と見分けが付くようオレンジ寄りにする */
  .badge-type-half-leave { background: #feebc8; color: #7b341e; }
  .badge-type-off { background: #edf2f7; color: #a0aec0; }
  .staff-detail-table .row-off td { color: #a0aec0; }

  /* Staff summary (footer - fixed at bottom) */
  .staff-summary {
    display: flex; flex-wrap: wrap; gap: 12px; padding: 14px 20px;
    border-top: 2px solid #e2e8f0; background: #f7fafc;
    flex-shrink: 0;
  }
  .summary-item {
    display: flex; flex-direction: column; align-items: center;
    padding: 8px 16px; background: #fff; border-radius: 8px; border: 1px solid #e2e8f0;
    min-width: 100px;
  }
  .summary-label2 { font-size: 11px; font-weight: 600; color: #718096; }
  .summary-value { font-size: 18px; font-weight: 700; color: #1a202c; }
  .summary-overtime { color: #d69e2e; }
  .summary-latenight { color: #6b46c1; }
  .summary-payroll { font-weight: 700; }
  .summary-amount { font-size: 13px; font-weight: 600; color: #718096; }

  .archive-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }
  .archive-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    margin: 0;
    cursor: pointer;
  }
  .archive-note {
    font-size: 11px;
    color: #718096;
  }
  .badge-archived {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 700;
    background: #fed7d7;
    color: #822727;
  }
</style>
